import React, { useMemo, useState } from 'react';
import { Box, Button, CircularProgress, CssBaseline, TextField, ThemeProvider, Typography } from '@mui/material';
import { createCustomTheme } from '../theme';
import { THEMES } from '../theme/constants';
import { CLOUD9_API } from '../config/constants';
import Logo from '../assets/images/logo.png';
import { getStoredTenant, normalizeTenant, setStoredTenant } from './platform';

/**
 * First screen of the native app: which workspace (tenant) to work in.
 *
 * On the web the tenant is the first label of the admin address (`delivery.admin.gain-intel.com`);
 * the app is served from `localhost`, so the user types it once. It is checked against the API
 * (an unknown tenant answers 404 "Organisation not found") and then the app reloads so every module
 * — and the stock microfrontends — start with it.
 *
 * The server dictionary cannot load before a tenant is known, so this one screen carries its own
 * three-language labels.
 */
const LABELS = {
  english: {
    title: 'Your workspace',
    subtitle: 'Enter your business workspace — the first part of your admin address.',
    example: 'For example, delivery for delivery.admin.{domain}',
    field: 'Workspace',
    action: 'Continue',
    invalid: 'Enter a valid workspace name.',
    notFound: "This workspace doesn't exist. Check the name and try again.",
    network: "Couldn't reach the server. Check your connection and try again.",
  },
  arabic: {
    title: 'مساحة العمل',
    subtitle: 'أدخل مساحة عمل نشاطك التجاري — الجزء الأول من عنوان لوحة الإدارة.',
    example: 'مثال: delivery لعنوان delivery.admin.{domain}',
    field: 'مساحة العمل',
    action: 'متابعة',
    invalid: 'أدخل اسم مساحة عمل صالحًا.',
    notFound: 'مساحة العمل هذه غير موجودة. تحقق من الاسم وحاول مرة أخرى.',
    network: 'تعذّر الوصول إلى الخادم. تحقق من الاتصال وحاول مرة أخرى.',
  },
  french: {
    title: 'Votre espace de travail',
    subtitle: "Saisissez l'espace de travail de votre entreprise — la première partie de votre adresse d'administration.",
    example: 'Par exemple, delivery pour delivery.admin.{domain}',
    field: 'Espace de travail',
    action: 'Continuer',
    invalid: "Saisissez un nom d'espace de travail valide.",
    notFound: "Cet espace de travail n'existe pas. Vérifiez le nom et réessayez.",
    network: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
  },
};

const deviceLanguage = () => {
  const lang = String((typeof navigator !== 'undefined' && navigator.language) || 'en').toLowerCase();
  if (lang.startsWith('ar')) return 'arabic';
  if (lang.startsWith('fr')) return 'french';
  return 'english';
};

/** 'ok' | 'not-found' | 'network' — an unknown tenant answers 404 on any tenant-scoped route. */
async function checkWorkspace(tenant) {
  try {
    const res = await fetch(`${CLOUD9_API}/globalconfig`, { headers: { 'incoming-origin': tenant } });
    if (res.ok) return 'ok';
    return res.status === 404 ? 'not-found' : 'network';
  } catch (e) {
    return 'network';
  }
}

export default function WorkspaceSetup() {
  const language = deviceLanguage();
  const t = LABELS[language];
  const domain = process.env.REACT_APP_DOMAIN_NAME || 'gain-intel.com';
  const theme = useMemo(
    () =>
      createCustomTheme({
        direction: language === 'arabic' ? 'rtl' : 'ltr',
        theme: window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ? THEMES.DARK : THEMES.LIGHT,
      }),
    [language]
  );

  const [value, setValue] = useState(getStoredTenant());
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    const tenant = normalizeTenant(value);
    if (!tenant) {
      setError(t.invalid);
      return;
    }
    setError('');
    setChecking(true);
    const result = await checkWorkspace(tenant);
    if (result !== 'ok') {
      setChecking(false);
      setError(result === 'not-found' ? t.notFound : t.network);
      return;
    }
    setStoredTenant(tenant);
    window.location.replace('/');
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box
        dir={language === 'arabic' ? 'rtl' : 'ltr'}
        sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default', px: 3 }}
      >
        <Box component="form" onSubmit={submit} noValidate sx={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box component="img" src={Logo} alt="" sx={{ width: 72, height: 72, objectFit: 'contain', alignSelf: 'center', mb: 1 }} />
          <Typography variant="h5" fontWeight={700} textAlign="center" color="text.primary">
            {t.title}
          </Typography>
          <Typography variant="body2" textAlign="center" color="text.secondary">
            {t.subtitle}
          </Typography>
          <TextField
            label={t.field}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              if (error) setError('');
            }}
            error={!!error}
            helperText={error || t.example.replace('{domain}', domain)}
            autoFocus
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            inputProps={{ inputMode: 'url', enterKeyHint: 'go', style: { direction: 'ltr' } }}
            fullWidth
          />
          <Button type="submit" variant="contained" size="large" disabled={checking || !value.trim()} sx={{ py: 1.25, fontWeight: 700 }}>
            {checking ? <CircularProgress size={22} color="inherit" /> : t.action}
          </Button>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
